import { Button, Grid, Stack, Typography } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { Header } from 'components/navigation/Header'
import { StatusCheckBreadcrumbs } from 'components/status-checks/common/StatusCheckBreadcrumbs'
import { EditStatusCheckFormContainer } from 'components/status-checks/edit/EditStatusCheckFormContainer'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import DeleteStatusCheck from 'components/status-checks/list/DeleteStatusCheck'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import toast from 'lib/toast'

import type { ServiceStatusCheck } from 'types/api/status-checks/status-check'

function isServiceStatusCheck(item: unknown): item is ServiceStatusCheck {
  return item instanceof Object && 'id' in item && 'integration' in item
}

export const EditStatusCheck: React.FC = () => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const { statusCheckId } = useParams<{
    statusCheckId: string
  }>()
  const location = useLocation()
  const { referrer = statusChecksUrls.base(), service } = location.state ?? {}
  const navigate = useNavigate()

  const {
    isError: hasErrorStatusChecks,
    isFetching: isFetchingStatusChecks,
    statusChecks: existingStatusChecks
  } = useStatusChecksContext()

  // Use passed in status check value if cloning a status check. Otherwise, the status check being edited should be
  // inferred from the URL params
  const selectedStatusCheck = existingStatusChecks.find(
    (existingStatusCheck) => existingStatusCheck.id === statusCheckId
  )

  return (
    <>
      <Helmet title="Edit Health Check" />
      <Header>
        <StatusCheckBreadcrumbs
          endLabel="Edit Health Check"
          referrer={referrer}
          service={service}
        />
      </Header>
      <PageContainer>
        <PageSection
          hasError={hasErrorStatusChecks}
          isLoading={isFetchingStatusChecks}
        >
          {() => {
            if (!isServiceStatusCheck(selectedStatusCheck)) {
              throw new Error(
                'The selected status check is not of type ServiceStatusCheck'
              )
            }

            return (
              <>
                <Stack mb={3} spacing={3}>
                  <Grid container gap={2}>
                    <Grid item>
                      <Typography
                        data-test-edit-status-check-title
                        pb={0}
                        variant="h5"
                      >
                        {`Edit ${selectedStatusCheck.name}`}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Stack direction="row" spacing={2}>
                        <Button
                          data-test-clone-status-check
                          component={StyledRouterLink}
                          data-track-action="clone-status-check"
                          data-track-category="status-checks"
                          data-track-on="click"
                          fullWidth={false}
                          size="small"
                          startIcon={<Icon icon="clone" />}
                          state={{
                            statusCheck: {
                              ...selectedStatusCheck,
                              // Unlike the clone button in `StatusCheckList` where the statusCheck passed
                              // in is of type StatusCheck, and will later get formatted to type ServiceStatusCheck,
                              // the selectedStatusCheck here is already of type ServiceStatusCheck,
                              // so we need to overwrite the id instead (and not identifier) since the consumer of
                              // this location state is checking the id field to determine whether the status check
                              // is a clone
                              id: null
                            }
                          }}
                          to={statusChecksUrls.new()}
                          variant="contained"
                        >
                          Clone
                        </Button>
                        <DeleteStatusCheck
                          statusCheck={selectedStatusCheck}
                          onChanged={async () => {
                            toast({
                              type: 'success',
                              text: `Successfully deleted Health Check ${selectedStatusCheck.name}`
                            })
                            navigate(statusChecksUrls.base())
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                  <Stack alignItems="baseline" direction="row" spacing={1}>
                    <Typography data-test-edit-status-check-title mr={1}>
                      Observability Tool:
                    </Typography>
                    <Typography>
                      {selectedStatusCheck.integration.name}
                    </Typography>
                    <Typography
                      component={RouterLink}
                      to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
                        selectedStatusCheck.integration
                      )}
                    >
                      <Icon icon="edit" />
                    </Typography>
                  </Stack>
                </Stack>
                <EditStatusCheckFormContainer
                  statusCheck={selectedStatusCheck}
                />
              </>
            )
          }}
        </PageSection>
      </PageContainer>
    </>
  )
}
